import type { ClaudeScope, ClaudeTool, ClaudeToolContext } from './types';

/**
 * In-memory tool registry. Addons call `registerTool()` at boot; the
 * `claude-chat` Edge Function filters by `allowedToolNames` + scope check.
 *
 * Registry is module-local — one registry per runtime. Server-side (Edge
 * Function) and client-side (Cmd+K command palette) each hold their own
 * view; tools meant for both register in both.
 */
const registry = new Map<string, ClaudeTool<unknown, unknown>>();

export const registerTool = <Input, Output>(tool: ClaudeTool<Input, Output>): void => {
  if (registry.has(tool.name)) {
    throw new Error(`[claude] tool "${tool.name}" already registered`);
  }
  registry.set(tool.name, tool as unknown as ClaudeTool<unknown, unknown>);
};

export const unregisterTool = (name: string): void => {
  registry.delete(name);
};

export const getTool = (name: string): ClaudeTool<unknown, unknown> | undefined =>
  registry.get(name);

/**
 * Tools visible to a given user context — filtered by scope grant.
 * Tools whose `requiredScopes` are not fully held by the user are hidden.
 */
export const listToolsForContext = (
  ctx: Pick<ClaudeToolContext, 'scopes'>,
): ClaudeTool<unknown, unknown>[] =>
  Array.from(registry.values()).filter((tool) =>
    tool.requiredScopes.every((scope) => ctx.scopes.includes(scope)),
  );

/** Test hook — clears the registry between tests. */
export const __resetToolRegistryForTests = (): void => {
  registry.clear();
};

export const hasScope = (ctx: Pick<ClaudeToolContext, 'scopes'>, scope: ClaudeScope): boolean =>
  ctx.scopes.includes(scope);
